import { logger } from '../utils/logger';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  BattleState,
  BattleUnitState,
  TurnOrderEntry,
  HotbarSkill,
  VictoryData,
  AttackForecast,
} from '../types/battle';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { isUnitStunned, getUnitAtPosition, getTileAtPosition, calculateWeaponRange, calculateSkillNodeTargets, canCastAnySkill, getCurrentTurnUnit, computeMovementPath, calculateAOEPreview, calculateThreatRange } from '../utils/battleUtils';
import BattleVictory from './BattleVictory';
import BattleDefeat from './BattleDefeat';
import BattleTileInspector from './BattleTileInspector';
import LevelUpBanner from './LevelUpBanner';
import BattleLegendPanel from './BattleLegendPanel';
import BattleLogPanel from './BattleLogPanel';
import BattleUnitInfoPanel from './BattleUnitInfoPanel';
import BattleTurnOrderPanel from './BattleTurnOrderPanel';
import BattleSidebarActions from './BattleSidebarActions';
import BattleActionsBar from './BattleActionsBar';
import BattleGridViewport from './BattleGridViewport';
import BattleMobileLayout from './BattleMobileLayout';
import { useBattleLog } from '../hooks/useBattleLog';
import { useGridControls } from '../hooks/useGridControls';
import { useBattleEffects } from '../hooks/useBattleEffects';
import { useBattleAnimation } from '../hooks/useBattleAnimation';
import { useBattleModes } from '../hooks/useBattleModes';
import { useBattleActions } from '../hooks/useBattleActions';
import { useIsMobile } from '../hooks/useIsMobile';
import { useConfirm } from './ConfirmModal';
import { makeAuthenticatedRequest } from '../utils/api';
import { playSfx } from '../utils/sound';
import { getBattleSpeed, setBattleSpeed, nextBattleSpeed, BattleSpeed } from '../utils/battleSpeed';


interface BattleGridProps {
  battleId: number;
  onExitBattle: () => void;
}


const BattleGrid: React.FC<BattleGridProps> = ({ battleId, onExitBattle }) => {
  const { handleAuthError, user } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial, isActive: isTutorialActive } = useTutorial();
  const isTutorialActiveRef = useRef(isTutorialActive);
  useEffect(() => { isTutorialActiveRef.current = isTutorialActive; }, [isTutorialActive]);
  const isMobile = useIsMobile();
  const [battle, setBattle] = useState<BattleState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<BattleUnitState | null>(null);
  // Touch targeting (GH-62): first tap previews the AOE/target, second tap on the
  // same tile confirms and fires the action. Desktop keeps hover-preview + single-click.
  const [pendingConfirmTile, setPendingConfirmTile] = useState<{ x: number; y: number } | null>(null);
  // Touch tile/unit inspector (GH-63): long-press opens a popover with the same info the
  // desktop `title` tooltips show, since touch has no hover to trigger those.
  const [inspectedTile, setInspectedTile] = useState<{ x: number; y: number } | null>(null);
  // Attack forecast (GH-66): hit%/damage range/crit% for the currently hovered (desktop) or
  // first-tapped (mobile) attack target, fetched from the preview endpoint.
  const [attackForecast, setAttackForecast] = useState<AttackForecast[] | null>(null);
  const forecastRequestRef = useRef(0);
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  // Enemy threat-range overlay (GH-69): 'off', 'all' (union of every living enemy), or a
  // specific enemy unit ID selected by tapping their turn-order portrait.
  const [threatOverlayMode, setThreatOverlayMode] = useState<'off' | 'all' | number>('off');
  const [battleSpeed, setBattleSpeedState] = useState<BattleSpeed>(getBattleSpeed);
  const [victoryData, setVictoryData] = useState<VictoryData>({ experience: 0, loot: [], tilesUnlocked: [] });
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(60);
  const turnTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { battleLog, setBattleLog, battleLogFilter, setBattleLogFilter, logContentRef, addToBattleLog, addEnhancedBattleLog } = useBattleLog();
  const { confirm, confirmDialog } = useConfirm();

  const {
    floatingTexts,
    passiveNotifications,
    fxEntries,
    unitReactions,
    dyingUnits,
    screenShaking,
    enemiesDefeatedCount,
    addFloatingText,
    addPassiveNotification,
    removeFloatingText,
    removePassiveNotification,
    removeFxEntry,
    spawnFx,
    showTurnTransition,
    levelUpBanner,
    triggerLevelUpCelebration,
    dismissLevelUpBanner,
    processPassiveEvents,
    triggerHitReaction,
    triggerLunge,
    triggerDeathAnimation,
    triggerScreenShake,
  } = useBattleEffects();

  const { processAIActions, animateUnitMovement } = useBattleAnimation(
    setBattle, addEnhancedBattleLog, addToBattleLog, addNotification,
    addFloatingText, spawnFx,
    triggerHitReaction, triggerLunge, triggerDeathAnimation, triggerScreenShake
  );

  // Stable ref so useBattleModes can call executeSkillDirect without a circular dep
  const executeSkillDirectRef = useRef<(skill: HotbarSkill, x: number, y: number) => Promise<void>>(async () => {});

  const {
    movementMode, attackMode, skillMode,
    validMoves, validTargets, weaponRange,
    skillTargets, skillRange, aoeArea, selectedSkill,
    itemMode, selectedItem, itemTargets,
    setMovementMode, setAttackMode, setSkillMode,
    setValidMoves, setValidTargets, setWeaponRange,
    setSkillTargets, setSkillRange, setAoeArea, setSelectedSkill,
    clearAllModes, clearSkillState, clearSelectionState,
    enterMovementMode, enterAttackMode,
    cancelMovement, cancelAttack, cancelSkill,
    selectSkillNode, cancelSkillNode,
    selectBattleItem, cancelItem,
  } = useBattleModes(
    battle, selectedUnit, battleId,
    setSelectedUnit, addEnhancedBattleLog, setActionError,
    (...args) => executeSkillDirectRef.current(...args)
  );

  // Reset the touch confirm-tap whenever the active mode/skill changes so a stale
  // pending tile from a previous mode can't be "confirmed" into the wrong action.
  useEffect(() => {
    setPendingConfirmTile(null);
    setAttackForecast(null);
  }, [attackMode, skillMode, itemMode, selectedSkill, selectedItem, selectedUnit]);

  // GH-66: fetch hit%/damage range/crit% for a would-be attack, shown while hovering
  // (desktop) or first-tapping (mobile) a valid target. requestId guards against a slower
  // earlier response clobbering a faster later one when the player sweeps across tiles.
  const fetchAttackForecast = useCallback(async (x: number, y: number) => {
    if (!selectedUnit || !battleId) { setAttackForecast(null); return; }
    const requestId = ++forecastRequestRef.current;
    try {
      const response = await makeAuthenticatedRequest(
        `/battles/${battleId}/attack-preview?attackerId=${selectedUnit.id}&targetX=${x}&targetY=${y}`
      );
      if (requestId !== forecastRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        setAttackForecast(data.forecasts ?? []);
      } else {
        setAttackForecast(null);
      }
    } catch (err) {
      if (requestId === forecastRequestRef.current) setAttackForecast(null);
    }
  }, [selectedUnit, battleId]);

  // AOE/target preview for a given tile - shared by desktop hover and mobile first-tap.
  const getAoePreviewForTile = useCallback((x: number, y: number): { x: number; y: number }[] => {
    if (attackMode && selectedUnit) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (target && validTargets.some(t => t.id === target.id)) {
        const weapon = selectedUnit.equipment?.weapon;
        const aoeShape = weapon?.aoeShape || 'Single';
        return calculateAOEPreview(selectedUnit.position, x, y, aoeShape);
      }
    }
    if (skillMode && selectedSkill && skillTargets.some(t => t.x === x && t.y === y)) {
      const aoe = selectedSkill.skill?.areaOfEffect || 0;
      if (aoe > 0) {
        const aoeTiles: { x: number; y: number }[] = [{ x, y }];
        for (let dx = -aoe; dx <= aoe; dx++) {
          for (let dy = -aoe; dy <= aoe; dy++) {
            if (dx === 0 && dy === 0) continue;
            if (Math.abs(dx) + Math.abs(dy) <= aoe) aoeTiles.push({ x: x + dx, y: y + dy });
          }
        }
        return aoeTiles;
      }
      return [{ x, y }];
    }
    return [];
  }, [attackMode, skillMode, selectedUnit, selectedSkill, validTargets, skillTargets, battle?.units]);

  const handleTileHover = (x: number, y: number) => {
    if (isMobile) return; // touch devices use tap-to-preview instead
    if (skillMode || attackMode) setAoeArea(getAoePreviewForTile(x, y));
    if (attackMode && selectedUnit) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (target && validTargets.some(t => t.id === target.id)) {
        fetchAttackForecast(x, y);
        return;
      }
    }
    setAttackForecast(null);
  };

  const handleTileLeave = () => {
    if (isMobile) return;
    if (skillMode || attackMode) setAoeArea([]);
    setAttackForecast(null);
  };

  const handleTileInspect = (x: number, y: number) => setInspectedTile({ x, y });

  // GH-69: union the reachable-move-and-attack tiles of whichever enemy/enemies are currently
  // selected for the threat overlay. Recomputed whenever the battle state changes so it stays
  // correct as enemies move/die turn to turn.
  const threatTiles = useMemo(() => {
    if (threatOverlayMode === 'off' || !battle) return [];
    const livingEnemies = (battle.units ?? []).filter(u => !u.isPlayerUnit && u.stats.currentHP > 0);
    const targets = threatOverlayMode === 'all'
      ? livingEnemies
      : livingEnemies.filter(u => u.id === threatOverlayMode);
    const tiles = new Map<string, { x: number; y: number }>();
    for (const enemy of targets) {
      for (const t of calculateThreatRange(enemy, battle)) tiles.set(`${t.x},${t.y}`, t);
    }
    return Array.from(tiles.values());
  }, [threatOverlayMode, battle]);

  const toggleThreatOverlayAll = () => setThreatOverlayMode(prev => prev === 'off' ? 'all' : 'off');
  const toggleThreatOverlayUnit = (unitId: number) =>
    setThreatOverlayMode(prev => prev === unitId ? 'off' : unitId);

  const {
    fetchBattleState,
    moveUnit,
    attackUnit,
    executeSkill,
    executeSkillDirect,
    consumeItem,
    endTurn,
    handleFleeBattle,
    equippedSkills,
    battleItems,
  } = useBattleActions({
    battleId, battle, selectedUnit, selectedSkill, selectedItem,
    setBattle, setSelectedUnit, setLoading, setError, setActionError,
    setShowVictoryScreen, setVictoryData,
    battleLog, setBattleLog, addToBattleLog, addEnhancedBattleLog,
    addFloatingText, spawnFx, processPassiveEvents, processAIActions, animateUnitMovement,
    triggerHitReaction, triggerLunge, triggerDeathAnimation, triggerScreenShake, showTurnTransition,
    clearAllModes, clearSkillState, cancelMovement, cancelAttack, cancelSkill, cancelItem,
    handleAuthError, addNotification, onExitBattle, isTutorialActiveRef, confirm,
  });

  // Keep ref in sync so useBattleModes.selectSkillNode always calls the latest version
  executeSkillDirectRef.current = executeSkillDirect;

  const {
    gridViewportSize,
    zoomLevel,
    panOffset,
    setPanOffset,
    isDragging,
    gridContainerRef,
    autoFit,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleResizeStart,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useGridControls();

  // Auto-fit grid to container once when battle data first loads
  const hasAutoFitted = useRef(false);
  useEffect(() => {
    if (battle && !hasAutoFitted.current) {
      hasAutoFitted.current = true;
      requestAnimationFrame(() => autoFit(battle.gridWidth ?? 10, battle.gridHeight ?? 10));
    }
  }, [battle]);

  // Re-fit on resize/rotation (GH-60): tile pixel size changes at CSS breakpoints
  // (80/50/40px), so a zoom computed before rotating leaves the grid mis-scaled otherwise.
  useEffect(() => {
    if (!battle) return;
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleViewportChange = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => autoFit(battle.gridWidth ?? 10, battle.gridHeight ?? 10), 150);
    };
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [battle?.gridWidth, battle?.gridHeight]);

  // Sync selectedUnit with latest battle state to include status effects and cooldowns
  useEffect(() => {
    if (battle && selectedUnit) {
      const updatedUnit = battle.units?.find(u => u.id === selectedUnit.id);
      if (updatedUnit) setSelectedUnit(updatedUnit);
    }
  }, [battle]);

  // Clear action errors when turn changes
  useEffect(() => {
    if (battle?.currentUnitIndex !== undefined) setActionError(null);
  }, [battle?.currentUnitIndex]);

  // Clear localStorage and show victory screen when battle ends
  useEffect(() => {
    if (battle && (battle.status === 'Victory' || battle.status === 'Defeat')) {
      // The tile inspector (GH-63) renders above the victory/defeat overlay's z-index
      // if left open, blocking it - close it out when the battle ends. Same for a
      // still-showing level-up banner (GH-75) - the victory/defeat screen takes over now.
      setInspectedTile(null);
      dismissLevelUpBanner();
      try {
        localStorage.removeItem(`battle_${battleId}_state`);
      } catch (err) {
        logger.warn('Failed to clear battle state:', err);
      }
      if (battle.status === 'Victory' && !showVictoryScreen) {
        // GH-75: this status-driven path (victory detected via a plain state poll rather
        // than a direct attack/skill response) used to hardcode 0 XP. battle.characterXP is
        // now always populated (see the backend GH-75/GH-67 fix), so total XP is derived
        // from it instead - BattleVictory only falls back to `experience` when
        // characterXP is empty, but summing it here keeps that fallback correct too.
        setVictoryData({
          experience: (battle.characterXP ?? []).reduce((sum, c) => sum + c.xpGained, 0),
          loot: battle.materialsAwarded || [],
          tilesUnlocked: battle.tilesUnlocked || [],
          characterXP: battle.characterXP || [],
        });
        setShowVictoryScreen(true);
      }
    }
  }, [battle, battleId, showVictoryScreen, dismissLevelUpBanner]);

  // Victory/defeat stinger - plays exactly once per battle regardless of which
  // code path (player action vs AI turn) drove the status to its end state.
  const battleEndSoundPlayed = useRef(false);
  useEffect(() => {
    if (battleEndSoundPlayed.current) return;
    if (battle?.status === 'Victory') {
      battleEndSoundPlayed.current = true;
      playSfx('victory');
    } else if (battle?.status === 'Defeat') {
      battleEndSoundPlayed.current = true;
      playSfx('defeat');
    }
  }, [battle?.status]);

  // Level-up celebration (GH-75): battle.characterXP is now always fresh (not just on
  // Victory - see GH-67/GH-75 backend fix), so this fires the moment a level-up happens
  // regardless of what caused it (attack, skill, a DoT tick, an AI kill). Tracks the
  // highest level already celebrated per character so re-polling the same state (or a
  // second level-up later in the same battle) doesn't replay/miss the celebration.
  // Keyed by characterId (not name - two of a player's own characters could share a display
  // name) so a second character's genuine level-up is never mistaken for one already shown.
  // Skipped once the battle is over: the victory screen already shows "LEVEL UP!" per
  // character inline, so celebrating the same event via this overlay too would just duplicate
  // it and fight the victory screen for the same z-index space.
  const celebratedLevelsRef = useRef<Record<number, number>>({});
  useEffect(() => {
    if (!battle?.characterXP || battle.status !== 'InProgress') return;
    for (const c of battle.characterXP) {
      if (!c.leveledUp) continue;
      const alreadyCelebratedUpTo = celebratedLevelsRef.current[c.characterId] ?? c.oldLevel;
      if (c.newLevel > alreadyCelebratedUpTo) {
        celebratedLevelsRef.current[c.characterId] = c.newLevel;
        triggerLevelUpCelebration(c.characterName, c.oldLevel, c.newLevel);
        playSfx('levelUp');
      }
    }
  }, [battle?.characterXP, battle?.status, triggerLevelUpCelebration]);

  useEffect(() => {
    fetchBattleState();
  }, [battleId, fetchBattleState]);

  useEffect(() => {
    if (battle && !loading) checkAndStartTutorial('battle-basics');
  }, [battle, loading, checkAndStartTutorial]);

  // Turn timer countdown
  useEffect(() => {
    setTurnTimeRemaining(60);
    if (turnTimerRef.current) {
      clearInterval(turnTimerRef.current);
      turnTimerRef.current = null;
    }
    if (battle?.status !== 'InProgress') return;

    turnTimerRef.current = setInterval(() => {
      setTurnTimeRemaining(prev => {
        if (isTutorialActiveRef.current) return prev;
        if (prev <= 1) {
          if (battle && currentTurnUnit) {
            addToBattleLog(`⏰ Time's up for ${currentTurnUnit.name}! Ending turn automatically.`);
            endTurn(currentTurnUnit).catch(err => logger.error('Error auto-ending turn:', err));
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
    };
  }, [battle?.currentUnitIndex, battle?.status]);

  // Keyboard controls for grid panning and action hotkeys
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

      const panSpeed = 50;
      if (event.key === 'ArrowUp') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x, y: prev.y + panSpeed })); return; }
      if (event.key === 'ArrowDown') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x, y: prev.y - panSpeed })); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x + panSpeed, y: prev.y })); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x - panSpeed, y: prev.y })); return; }

      if (!selectedUnit?.isPlayerUnit) return;

      const key = event.key;
      if (key === '`' || key === '~') {
        event.preventDefault();
        const weaponAPCost = selectedUnit.equipment?.weapon?.weaponAPCost || 2;
        if (selectedUnit.equipment?.weapon && !attackMode && selectedUnit.stats.currentAP >= weaponAPCost) {
          enterAttackMode();
        }
        return;
      }

      let skillSlotIndex = -1;
      if (key >= '1' && key <= '9') skillSlotIndex = parseInt(key) - 1;
      else if (key === '0') skillSlotIndex = 9;
      if (skillSlotIndex === -1) return;

      event.preventDefault();
      const equippedSkill = equippedSkills.find(es => es.hotbarSlot === skillSlotIndex);
      if (equippedSkill) {
        const skill = equippedSkill.skill;
        const cooldownInfo = selectedUnit.skillCooldowns?.find(cd => cd.skillNodeId === Number(skill.id));
        const isOnCooldown = cooldownInfo && cooldownInfo.turnsRemaining > 0;
        if (!isOnCooldown && !isUnitStunned(selectedUnit) && selectedUnit.stats.currentAP >= (skill.apCost || 0)) {
          selectSkillNode(equippedSkill);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedUnit, equippedSkills, attackMode, skillMode, panOffset]);

  const handleTileClick = (x: number, y: number) => {
    if (battle?.status === 'Victory' || battle?.status === 'Defeat') return;
    if (movementMode && selectedUnit) {
      if (validMoves.some(m => m.x === x && m.y === y)) {
        const path = computeMovementPath(selectedUnit.position, { x, y }, validMoves);
        moveUnit(selectedUnit.id, x, y, path);
      }
      return;
    }
    if (attackMode && selectedUnit) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (!target || !validTargets.some(t => t.id === target.id)) return;
      if (isMobile) {
        const isConfirming = pendingConfirmTile?.x === x && pendingConfirmTile?.y === y;
        if (!isConfirming) {
          setAoeArea(getAoePreviewForTile(x, y));
          setPendingConfirmTile({ x, y });
          fetchAttackForecast(x, y);
          return;
        }
        setPendingConfirmTile(null);
        setAttackForecast(null);
      }
      attackUnit(selectedUnit.id, target.id);
      return;
    }
    if (skillMode && selectedSkill && selectedUnit) {
      if (!skillTargets.some(t => t.x === x && t.y === y)) return;
      if (isMobile) {
        const isConfirming = pendingConfirmTile?.x === x && pendingConfirmTile?.y === y;
        if (!isConfirming) {
          setAoeArea(getAoePreviewForTile(x, y));
          setPendingConfirmTile({ x, y });
          return;
        }
        setPendingConfirmTile(null);
      }
      executeSkill(x, y);
      return;
    }
    if (skillMode && !selectedSkill) {
      setActionError('Please select a skill first, or click Cancel Skill to exit skill mode.');
      return;
    }
    if (itemMode && selectedItem) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (!target || !itemTargets.some(t => t.id === target.id)) return;
      if (isMobile) {
        const isConfirming = pendingConfirmTile?.x === x && pendingConfirmTile?.y === y;
        if (!isConfirming) {
          setPendingConfirmTile({ x, y });
          return;
        }
        setPendingConfirmTile(null);
      }
      consumeItem(target.id);
      return;
    }

    const unit = getUnitAtPosition(battle?.units ?? [], x, y);
    if (!unit) { clearSelectionState(null); return; }

    const ctu = getCurrentTurnUnit(battle);
    if (unit.isPlayerUnit && ctu && ctu.unitId === unit.id) {
      clearSelectionState(unit);
      setActionError(null);
    } else if (unit.isPlayerUnit && ctu && ctu.unitId !== unit.id) {
      addEnhancedBattleLog('error', { message: `❌ It's ${ctu.name}'s turn, not ${unit.name}'s turn!` });
      setActionError(`It's ${ctu.name}'s turn. You can only control the active unit.`);
    } else if (!unit.isPlayerUnit) {
      clearSelectionState(unit);
    }
  };

  if (loading) return <div className="battle-loading">Loading battle...</div>;
  if (error) return (
    <div className="battle-error">
      <p>Error: {error}</p>
      <button onClick={onExitBattle}>Return to World Map</button>
    </div>
  );
  if (!battle) return (
    <div className="battle-error">
      <p>Battle not found</p>
      <button onClick={onExitBattle}>Return to World Map</button>
    </div>
  );

  const currentTurnUnit = getCurrentTurnUnit(battle);
  const displayUnit = selectedUnit || (currentTurnUnit ? battle.units?.find(u => u.id === currentTurnUnit.unitId) : null);
  const isDisplayUnitCurrentTurn = !selectedUnit && !!currentTurnUnit;

  const tileInspectorModal = inspectedTile && (
    <BattleTileInspector
      x={inspectedTile.x}
      y={inspectedTile.y}
      tile={getTileAtPosition(battle.tiles ?? [], inspectedTile.x, inspectedTile.y)}
      unit={getUnitAtPosition(battle.units ?? [], inspectedTile.x, inspectedTile.y)}
      onClose={() => setInspectedTile(null)}
    />
  );

  const levelUpBannerOverlay = levelUpBanner && (
    <LevelUpBanner
      characterName={levelUpBanner.characterName}
      oldLevel={levelUpBanner.oldLevel}
      newLevel={levelUpBanner.newLevel}
    />
  );

  const gridViewportProps = {
    gridWidth: battle.gridWidth,
    gridHeight: battle.gridHeight,
    tiles: battle.tiles,
    units: battle.units,
    selectedUnit,
    currentTurnUnit,
    validMoves,
    validTargets,
    skillTargets,
    skillRange,
    aoeArea,
    weaponRange,
    attackMode,
    skillMode,
    selectedSkill,
    itemTargets,
    unitReactions,
    dyingUnits,
    pendingConfirmTile,
    attackForecast,
    threatTiles,
    onTileClick: handleTileClick,
    onTileHover: handleTileHover,
    onTileLeave: handleTileLeave,
    onTileInspect: handleTileInspect,
    screenShaking,
    gridContainerRef,
    isDragging,
    zoomLevel,
    panOffset,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    floatingTexts,
    passiveNotifications,
    onFloatingTextComplete: removeFloatingText,
    onPassiveNotificationComplete: removePassiveNotification,
    fxEntries,
    onFxComplete: removeFxEntry,
  };

  if (isMobile) {
    return (
      <div className="battle-screen">
        {confirmDialog}
        {tileInspectorModal}
        {levelUpBannerOverlay}

        {showVictoryScreen && battle.status === 'Victory' && (
          <BattleVictory
            experience={victoryData.experience}
            gold={victoryData.gold}
            loot={victoryData.loot.map(m => ({ name: m.materialName, quantity: m.quantity, rarity: m.rarity || 'Common' }))}
            tilesUnlocked={victoryData.tilesUnlocked}
            bonuses={victoryData.bonuses}
            characterXP={victoryData.characterXP}
            onContinue={onExitBattle}
          />
        )}

        {battle.status === 'Defeat' && (
          <BattleDefeat
            battleName={battle.battleName}
            turnsSurvived={battle.currentTurn ?? 0}
            enemiesDefeated={enemiesDefeatedCount}
            onContinue={onExitBattle}
          />
        )}

        <BattleMobileLayout
          battle={battle}
          currentTurnUnit={currentTurnUnit}
          selectedUnit={selectedUnit}
          displayUnit={displayUnit ?? null}
          isDisplayUnitCurrentTurn={isDisplayUnitCurrentTurn}
          turnTimeRemaining={turnTimeRemaining}
          attackMode={attackMode}
          skillMode={skillMode}
          movementMode={movementMode}
          itemMode={itemMode}
          selectedSkill={selectedSkill}
          selectedItem={selectedItem}
          validTargets={validTargets}
          skillTargets={skillTargets}
          actionError={actionError}
          battleItems={battleItems}
          equippedSkills={equippedSkills}
          onAttackUnit={attackUnit}
          onExecuteSkill={executeSkill}
          onSelectUnit={(unit) => {
            setSelectedUnit(unit);
            setMovementMode(false);
            setAttackMode(false);
            setSkillMode(false);
            setValidMoves([]);
            setValidTargets([]);
            setSkillTargets([]);
            setWeaponRange([]);
            setSkillRange([]);
          }}
          onEnterAttackMode={enterAttackMode}
          onEnterMovementMode={enterMovementMode}
          onSelectSkillNode={selectSkillNode}
          onShowWeaponRange={(unit) => setWeaponRange(battle ? calculateWeaponRange(unit, battle) : [])}
          onClearWeaponRange={() => setWeaponRange([])}
          onShowSkillRange={(skill) => { if (selectedUnit && battle) { const { rangePositions } = calculateSkillNodeTargets(skill, selectedUnit, battle); setSkillRange(rangePositions); } }}
          onClearSkillRange={() => setSkillRange([])}
          onCancelMovement={cancelMovement}
          onCancelAttack={cancelAttack}
          onCancelSkill={cancelSkill}
          onCancelItem={cancelItem}
          onSelectBattleItem={selectBattleItem}
          onEndTurn={endTurn}
          onClearActionError={() => setActionError(null)}
          onInstantWin={user?.role !== 'Admin' ? undefined : async () => {
            try {
              const response = await makeAuthenticatedRequest(`/battles/${battleId}/instant-win`, { method: 'POST' });
              if (response.ok) {
                const result = await response.json();
                setBattle(result.battleState);
                setVictoryData({ experience: result.experience || 0, gold: result.goldAwarded || 0, loot: result.materialsAwarded || [], tilesUnlocked: result.tilesUnlocked || [], bonuses: result.bonuses || null });
                setShowVictoryScreen(true);
                addEnhancedBattleLog('victory', {});
              }
            } catch (err) {
              logger.error('Instant win failed:', err);
            }
          }}
          battleLog={battleLog}
          battleLogFilter={battleLogFilter}
          onBattleLogFilterChange={setBattleLogFilter}
          logContentRef={logContentRef}
          battleSpeed={battleSpeed}
          onCycleBattleSpeed={() => {
            const next = nextBattleSpeed(battleSpeed);
            setBattleSpeed(next);
            setBattleSpeedState(next);
          }}
          onFleeBattle={handleFleeBattle}
          showLegend={showLegend}
          onToggleLegend={() => setShowLegend(!showLegend)}
          threatOverlayMode={threatOverlayMode}
          onToggleThreatOverlayAll={toggleThreatOverlayAll}
          onToggleThreatUnit={toggleThreatOverlayUnit}
          gridViewportProps={gridViewportProps}
        />

        {showLegend && <BattleLegendPanel onClose={() => setShowLegend(false)} />}
      </div>
    );
  }

  return (
    <div className="battle-screen">
      {confirmDialog}
      {tileInspectorModal}
      {levelUpBannerOverlay}

      {showVictoryScreen && battle.status === 'Victory' && (
        <BattleVictory
          experience={victoryData.experience}
          gold={victoryData.gold}
          loot={victoryData.loot.map(m => ({ name: m.materialName, quantity: m.quantity, rarity: m.rarity || 'Common' }))}
          tilesUnlocked={victoryData.tilesUnlocked}
          bonuses={victoryData.bonuses}
          characterXP={victoryData.characterXP}
          onContinue={onExitBattle}
        />
      )}

      {battle.status === 'Defeat' && (
        <BattleDefeat
          battleName={battle.battleName}
          turnsSurvived={battle.currentTurn ?? 0}
          enemiesDefeated={enemiesDefeatedCount}
          onContinue={onExitBattle}
        />
      )}

      <div className="battle-left-section">
      <div className="battle-content">
        <div className="battle-left-column">

        {/* TOP BAR: battle location + turn order */}
        <div className="battle-top-bar">
          <h4 className="battle-top-title">{battle.battleName}</h4>
          <BattleTurnOrderPanel
            turnOrder={battle.turnOrder ?? []}
            units={battle.units ?? []}
            currentTurnUnit={currentTurnUnit}
            attackMode={attackMode}
            skillMode={skillMode}
            validTargets={validTargets}
            skillTargets={skillTargets}
            selectedUnit={selectedUnit}
            selectedSkill={selectedSkill}
            battleStatus={battle.status}
            turnTimeRemaining={turnTimeRemaining}
            showTimer={false}
            onAttackUnit={attackUnit}
            onExecuteSkill={executeSkill}
            onSelectUnit={(unit) => {
              setSelectedUnit(unit);
              setMovementMode(false);
              setAttackMode(false);
              setSkillMode(false);
              setValidMoves([]);
              setValidTargets([]);
              setSkillTargets([]);
              setWeaponRange([]);
              setSkillRange([]);
            }}
            threatOverlayUnitId={typeof threatOverlayMode === 'number' ? threatOverlayMode : null}
            onToggleThreatUnit={toggleThreatOverlayUnit}
          />
        </div>

        {/* GRID */}
        <div className="battle-grid-wrapper">
          <BattleGridViewport {...gridViewportProps} />
        </div>

        {/* ACTION AREA: skill hotbar (left) + timer/flee/legend stack (right) */}
        <div className="battle-action-area">
          <div className="battle-action-hotbar">
            {selectedUnit?.isPlayerUnit && (
              <BattleActionsBar
                selectedUnit={selectedUnit}
                currentTurnUnit={currentTurnUnit}
                attackMode={attackMode}
                skillMode={skillMode}
                selectedSkill={selectedSkill}
                equippedSkills={equippedSkills}
                onEnterAttackMode={enterAttackMode}
                onSelectSkillNode={selectSkillNode}
                onShowWeaponRange={(unit) => setWeaponRange(battle ? calculateWeaponRange(unit, battle) : [])}
                onClearWeaponRange={() => setWeaponRange([])}
                onShowSkillRange={(skill) => { if (selectedUnit && battle) { const { rangePositions } = calculateSkillNodeTargets(skill, selectedUnit, battle); setSkillRange(rangePositions); } }}
                onClearSkillRange={() => setSkillRange([])}
              />
            )}
          </div>
          <div className="battle-action-utility">
            {battle.status === 'InProgress' && currentTurnUnit && (
              <div className={`turn-timer ${turnTimeRemaining <= 10 ? 'timer-warning' : ''}`}>
                <span className="timer-icon">⏱️</span>
                <span className="timer-text">{turnTimeRemaining}s</span>
              </div>
            )}
            <button onClick={handleFleeBattle} className="exit-battle-btn">Flee</button>
            <button
              onClick={() => {
                const next = nextBattleSpeed(battleSpeed);
                setBattleSpeed(next);
                setBattleSpeedState(next);
              }}
              className="legend-toggle-btn"
              title="Cycle AI turn playback speed (1x / 2x / instant)"
            >
              Speed: {battleSpeed === 'instant' ? 'Instant' : battleSpeed}
            </button>
            <button
              onClick={toggleThreatOverlayAll}
              className={`legend-toggle-btn${threatOverlayMode !== 'off' ? ' threat-toggle-active' : ''}`}
              title="Show/hide every enemy's move+attack danger zone"
            >
              {threatOverlayMode !== 'off' ? 'Hide Threat Range' : '🚨 Threat Range'}
            </button>
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="legend-toggle-btn"
              title="Show/hide tile color legend"
            >
              {showLegend ? 'Hide Legend' : 'Legend'}
            </button>
          </div>
        </div>
        </div>{/* end battle-left-column */}
      </div>{/* end battle-content */}
      </div>{/* end battle-left-section */}

        {/* RIGHT PANEL: unit info + actions + log */}
        <div className="battle-right-panel">
          {displayUnit && (
            <BattleUnitInfoPanel displayUnit={displayUnit} isCurrentTurn={isDisplayUnitCurrentTurn} />
          )}

          <BattleSidebarActions
            movementMode={movementMode}
            attackMode={attackMode}
            skillMode={skillMode}
            itemMode={itemMode}
            selectedUnit={selectedUnit}
            currentTurnUnit={currentTurnUnit}
            battleStatus={battle.status}
            selectedSkill={selectedSkill}
            selectedItem={selectedItem}
            battleItems={battleItems}
            actionError={actionError}
            onCancelMovement={cancelMovement}
            onCancelAttack={cancelAttack}
            onCancelSkill={cancelSkill}
            onCancelItem={cancelItem}
            onEnterMovementMode={enterMovementMode}
            onEnterAttackMode={enterAttackMode}
            onSelectBattleItem={selectBattleItem}
            onEndTurn={endTurn}
            onClearActionError={() => setActionError(null)}
            onInstantWin={user?.role !== 'Admin' ? undefined : async () => {
              try {
                const response = await makeAuthenticatedRequest(`/battles/${battleId}/instant-win`, { method: 'POST' });
                if (response.ok) {
                  const result = await response.json();
                  setBattle(result.battleState);
                  setVictoryData({ experience: result.experience || 0, gold: result.goldAwarded || 0, loot: result.materialsAwarded || [], tilesUnlocked: result.tilesUnlocked || [], bonuses: result.bonuses || null });
                  setShowVictoryScreen(true);
                  addEnhancedBattleLog('victory', {});
                }
              } catch (err) {
                logger.error('Instant win failed:', err);
              }
            }}
          />

          <BattleLogPanel
            entries={battleLog}
            filter={battleLogFilter}
            onFilterChange={setBattleLogFilter}
            logContentRef={logContentRef}
          />
        </div>

      {showLegend && <BattleLegendPanel onClose={() => setShowLegend(false)} />}
    </div>
  );
};

export default BattleGrid;
